import * as React from "react";
import { AdapterLuxon } from "@mui/x-date-pickers-pro/AdapterLuxon";
import { Box, Button, List, ListItem, TextField, Typography } from "@mui/material";
import {
  DateRange,
  StaticDateRangePicker,
  LocalizationProvider,
  PickersShortcutsItem,
  PickersShortcutsProps,
  DateRangePickerDay,
  StaticDateRangePickerProps,
  PickersActionBarProps,
  DateRangePickerDayProps,
  usePickerActionsContext,
  usePickerContext,
  useIsValidValue,
} from "@mui/x-date-pickers-pro";
import { DateTime } from "luxon";
import { lighten, styled } from "@mui/material/styles";
import { useState } from "react";
import { lookBackShortcutItems } from "./DateRangeCalendarFilterShortcuts";
import type { TelemetryPropsInput } from "../instrumentation/gtm";
import { telemetryProps } from "../instrumentation/gtm";

export interface DateRangeCalendarValueProps {
  label?: string;
  value: DateRange<DateTime>;
}

/**
 * Bridges the typed-input toolbar and the action bar (sibling picker slots).
 * The toolbar publishes a `commitAndValidate` callback so the action bar's
 * Apply path can re-validate any pending draft and refuse acceptance while an
 * entry is invalid, instead of silently accepting the last committed range.
 */
interface DateDraftGate {
  commitAndValidate: () => boolean;
}
const DateDraftGateContext =
  React.createContext<React.MutableRefObject<DateDraftGate | null> | null>(null);
interface DateRangeCalendarFilterProps extends StaticDateRangePickerProps {
  onCancel?: () => void;
  onAccept?: () => void;
  activeShortcutLabel?: string;
  dateRangeShortcutItems?: PickersShortcutsItem<DateRange<DateTime>>[];
  /**
   * FilterBar V2 flag. When enabled, the date panel shows the typed start/end
   * date inputs (with Clear and validation) and left-aligns the left-hand range
   * shortcuts with a slightly smaller font to match the V2 filter design. When
   * disabled, the panel falls back to the calendar-only layout with the toolbar
   * hidden.
   */
  filterBarV2Enabled?: boolean;
  telemetry?: {
    accept?: TelemetryPropsInput;
    cancel?: TelemetryPropsInput;
  };
}

const StyledRangedDateCalendar = styled(DateRangePickerDay)(
  ({ theme, isHighlighting, outsideCurrentMonth }) => ({
    ...(!outsideCurrentMonth &&
      isHighlighting && {
        backgroundColor: lighten(theme.palette.secondary.main, theme.palette.mode === "dark" ? 0.33 : 0.88),
        color: theme.palette.common.white,
        "&:hover, &:focus": {
          backgroundColor: lighten(theme.palette.secondary.main, theme.palette.mode === "dark" ? 0.33 : 0.88),
        },
      }),
    ...{
      "& .MuiDateRangePickerDay-day.Mui-selected": {
        backgroundColor: theme.palette.secondary.main,
      },
    },
  }),
) as React.ComponentType<DateRangePickerDayProps>;

interface CustomActionBarProps extends PickersActionBarProps {
  onAccept?: () => void;
  onCancel?: () => void;
  telemetry?: DateRangeCalendarFilterProps["telemetry"];
}

const CustomActionBar = (props: CustomActionBarProps) => {
  const { onAccept, onCancel, telemetry } = props;
  const draftGateRef = React.useContext(DateDraftGateContext);

  // When the typed-input toolbar is present (V2), re-validate its drafts before
  // accepting so a pending invalid entry surfaces its error and blocks the
  // popover from closing on the previously committed range.
  const handleApply = () => {
    if (draftGateRef?.current && !draftGateRef.current.commitAndValidate()) {
      return;
    }
    onAccept?.();
  };

  return (
    <Box
      display={"flex"}
      justifyContent={"space-between"}
      sx={{
        gridRow: 3,
        width: "100%",
        // Picker layout root is a 3-column grid; span all of it.
        gridColumn: "1 / 4",
        padding: (theme) => theme.spacing(0.5, 2),
        borderTop: "1px solid",
        borderTopColor: (theme) => theme.palette.border,
      }}
    >
      <Button
        color={"inherit"}
        onClick={onCancel}
        {...(telemetry?.cancel ? telemetryProps(telemetry.cancel) : {})}
      >
        Cancel
      </Button>
      <Button
        onClick={handleApply}
        {...(telemetry?.accept ? telemetryProps(telemetry.accept) : {})}
      >
        Apply
      </Button>
    </Box>
  );
};

interface CustomRangeShortcutsProps
  extends PickersShortcutsProps<DateRange<DateTime>> {
  selectedLabel?: string;
  onShortcutSelected?: (label: string) => void;
  filterBarV2Enabled?: boolean;
}

function CustomRangeShortcuts(props: CustomRangeShortcutsProps) {
  const {
    items,
    changeImportance = "accept",
    selectedLabel,
    onShortcutSelected,
    filterBarV2Enabled = false,
  } = props;
  // MUI X v8 moved shortcut selection/validation from slot props to picker context.
  const { setValue } = usePickerActionsContext<DateRange<DateTime>>();
  const isValidValue = useIsValidValue<DateRange<DateTime>>();

  if (items == null || items.length === 0) {
    return null;
  }

  const resolvedItems = items.map((item) => {
    const newValue = item.getValue({ isValid: isValidValue });
    return {
      label: item.label,
      onClick: () => {
        setValue(newValue, {
          changeImportance,
          shortcut: item,
          source: "view",
        });
        onShortcutSelected?.(item.label);
      },
      disabled: !isValidValue(newValue),
    };
  });

  return (
    <Box
      sx={{
        paddingY: 1,
        display: "flex",
        gridRow: 2,
        gridColumn: 1,
        alignSelf: "stretch",
        borderRight: 1,
        borderColor: "divider",
      }}
    >
      <List
        dense
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        {resolvedItems.map((item) => {
          return (
            <ListItem
              key={item.label}
              sx={{
                justifyContent: filterBarV2Enabled ? "flex-start" : "center",
                color:
                  item.label === selectedLabel
                    ? "secondary.main"
                    : "text.secondary",
              }}
            >
              <Button
                color={"inherit"}
                onClick={item.onClick}
                disabled={item.disabled}
                sx={
                  filterBarV2Enabled
                    ? {
                        justifyContent: "flex-start",
                        fontSize: (theme) =>
                          theme.typography.caption.fontSize,
                      }
                    : undefined
                }
              >
                {item.label}
              </Button>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
}

const DATE_INPUT_FORMAT = "M/d/yyyy";

const formatDateInput = (dt: DateTime | null | undefined): string =>
  dt && dt.isValid ? dt.toFormat(DATE_INPUT_FORMAT) : "";

/**
 * Custom toolbar slot that renders typed start/end date inputs above the calendar.
 * Reads/writes the controlled range through the v8 picker context so calendar
 * picks and presets stay in sync with the text inputs, and enforces the same
 * min/max window the calendar uses via useIsValidValue.
 */
function CustomDateRangeToolbar() {
  const { value } = usePickerContext<DateRange<DateTime>>();
  const { setValue } = usePickerActionsContext<DateRange<DateTime>>();
  const isValidValue = useIsValidValue<DateRange<DateTime>>();
  const draftGateRef = React.useContext(DateDraftGateContext);

  const [start, end] = value ?? [null, null];
  const nextStartText = formatDateInput(start);
  const nextEndText = formatDateInput(end);

  const [startText, setStartText] = useState<string>(nextStartText);
  const [endText, setEndText] = useState<string>(nextEndText);
  const [startError, setStartError] = useState<string | null>(null);
  const [endError, setEndError] = useState<string | null>(null);

  const setFieldError = (which: "start" | "end", message: string | null) => {
    if (which === "start") {
      setStartError(message);
    } else {
      setEndError(message);
    }
  };

  // Re-sync the inputs whenever the committed range changes from the calendar,
  // a preset/shortcut, or a cancel. Depending on the derived strings keeps this
  // effect stable even if the picker context recreates the value array.
  React.useEffect(() => {
    setStartText(nextStartText);
    setEndText(nextEndText);
    setStartError(null);
    setEndError(null);
  }, [nextStartText, nextEndText]);

  const buildCandidate = (
    which: "start" | "end",
    parsed: DateTime,
  ): DateRange<DateTime> =>
    which === "start"
      ? [parsed.startOf("day"), end]
      : [start, parsed.endOf("day")];

  // Live update while typing: only commit when the value is a complete, valid,
  // in-window date. Never surface an error mid-typing and never mutate the
  // committed range on a partial/invalid entry.
  const handleChange = (which: "start" | "end", text: string) => {
    if (which === "start") {
      setStartText(text);
    } else {
      setEndText(text);
    }
    const trimmed = text.trim();
    if (trimmed === "") {
      return;
    }
    const parsed = DateTime.fromFormat(trimmed, DATE_INPUT_FORMAT);
    if (!parsed.isValid) {
      return;
    }
    const candidate = buildCandidate(which, parsed);
    if (!isValidValue(candidate)) {
      return;
    }
    setFieldError(which, null);
    setValue(candidate, { changeImportance: "set" });
  };

  // Commit-time validation on blur/Enter: show a message for an invalid format,
  // a reversed range, or an out-of-window date, and leave the committed range
  // untouched. Only the offending field is flagged. Pure: sets the field error
  // and returns validity WITHOUT mutating the committed range, so it can be
  // reused by the Apply gate without side-effecting the other field's state.
  const validate = (which: "start" | "end", text: string): boolean => {
    const trimmed = text.trim();
    if (trimmed === "") {
      setFieldError(which, null);
      return true;
    }
    const parsed = DateTime.fromFormat(trimmed, DATE_INPUT_FORMAT);
    if (!parsed.isValid) {
      setFieldError(which, "Enter a valid date (M/D/YYYY).");
      return false;
    }
    const candidate = buildCandidate(which, parsed);
    if (!isValidValue(candidate)) {
      const [candidateStart, candidateEnd] = candidate;
      const isReversed =
        candidateStart != null &&
        candidateEnd != null &&
        candidateStart > candidateEnd;
      setFieldError(
        which,
        isReversed
          ? "Start date must be on or before the end date."
          : "Date is outside the allowed range.",
      );
      return false;
    }
    setFieldError(which, null);
    return true;
  };

  // Blur/Enter commit: validate, then push a valid entry into the committed
  // range (idempotent with the live handleChange commit).
  const commit = (which: "start" | "end", text: string): boolean => {
    if (!validate(which, text)) {
      return false;
    }
    const trimmed = text.trim();
    if (trimmed !== "") {
      const parsed = DateTime.fromFormat(trimmed, DATE_INPUT_FORMAT);
      setValue(buildCandidate(which, parsed), { changeImportance: "set" });
    }
    return true;
  };

  const handleKeyDown = (
    which: "start" | "end",
    event: React.KeyboardEvent<HTMLInputElement>,
    text: string,
  ) => {
    if (event.key === "Enter") {
      commit(which, text);
    }
  };

  // Publish a validation gate the action bar's Apply path can call. No
  // dependency array: re-registering each render keeps the closure over the
  // latest draft text and commit fn, and both fields are committed before
  // combining so an error surfaces on each invalid field.
  React.useEffect(() => {
    if (draftGateRef == null) {
      return;
    }
    draftGateRef.current = {
      commitAndValidate: () => {
        const startOk = validate("start", startText);
        const endOk = validate("end", endText);
        return startOk && endOk;
      },
    };
    return () => {
      draftGateRef.current = null;
    };
  });

  // Clear resets the draft range and the typed inputs without accepting:
  // clearValue() is an accept-level change that would fire onAccept and close
  // the host dialog, so results must only change on Apply.
  const handleClear = () => {
    setValue([null, null], { changeImportance: "set" });
    setStartText("");
    setEndText("");
    setStartError(null);
    setEndError(null);
  };

  return (
    <Box
      sx={{
        gridRow: 1,
        gridColumn: "1 / 4",
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        padding: (theme) => theme.spacing(2, 2),
        borderBottom: "1px solid",
        borderBottomColor: (theme) => theme.palette.border,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <TextField
          size="small"
          label="Start date"
          placeholder="M/D/YYYY"
          value={startText}
          error={Boolean(startError)}
          onChange={(event) => handleChange("start", event.target.value)}
          onBlur={(event) => commit("start", event.target.value)}
          onKeyDown={(event) =>
            handleKeyDown(
              "start",
              event as React.KeyboardEvent<HTMLInputElement>,
              (event.target as HTMLInputElement).value,
            )
          }
          slotProps={{
            htmlInput: {
              "aria-label": "Start date",
              ...telemetryProps({
                name: "date_range_start_input",
                section: "filters",
                label: "Start date",
              }),
            },
          }}
        />
        <Typography aria-hidden sx={{ color: "text.secondary" }}>
          &ndash;
        </Typography>
        <TextField
          size="small"
          label="End date"
          placeholder="M/D/YYYY"
          value={endText}
          error={Boolean(endError)}
          onChange={(event) => handleChange("end", event.target.value)}
          onBlur={(event) => commit("end", event.target.value)}
          onKeyDown={(event) =>
            handleKeyDown(
              "end",
              event as React.KeyboardEvent<HTMLInputElement>,
              (event.target as HTMLInputElement).value,
            )
          }
          slotProps={{
            htmlInput: {
              "aria-label": "End date",
              ...telemetryProps({
                name: "date_range_end_input",
                section: "filters",
                label: "End date",
              }),
            },
          }}
        />
        <Button
          color={"primary"}
          onClick={handleClear}
          sx={{ marginLeft: "auto" }}
          {...telemetryProps({
            name: "date_range_clear",
            section: "filters",
            label: "Clear",
          })}
        >
          Clear
        </Button>
      </Box>
      {startError || endError ? (
        <Typography role="alert" variant="caption" sx={{ color: "error.main" }}>
          {startError ?? endError}
        </Typography>
      ) : null}
    </Box>
  );
}

function DateRangeCalendarFilter(props: DateRangeCalendarFilterProps) {
  const { activeShortcutLabel, dateRangeShortcutItems, filterBarV2Enabled } =
    props;
  const [selectedLabel, setSelectedLabel] = useState(activeShortcutLabel);
  const draftGateRef = React.useRef<DateDraftGate | null>(null);

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <DateDraftGateContext.Provider value={draftGateRef}>
        <StaticDateRangePicker
          slots={{
            actionBar: CustomActionBar,
            shortcuts: CustomRangeShortcuts,
            day: StyledRangedDateCalendar,
            ...(filterBarV2Enabled ? { toolbar: CustomDateRangeToolbar } : {}),
          }}
          slotProps={{
            shortcuts: {
              items: dateRangeShortcutItems ?? lookBackShortcutItems,
              changeImportance: "set",
              selectedLabel,
              onShortcutSelected: setSelectedLabel,
              filterBarV2Enabled,
            } as CustomRangeShortcutsProps,
            actionBar: {
              onCancel: props.onCancel,
              onAccept: props.onAccept,
              telemetry: props.telemetry,
            } as CustomActionBarProps,
            ...(filterBarV2Enabled ? {} : { toolbar: { hidden: true } }),
            layout: {
              sx: {
                "& .MuiPickersLayout-contentWrapper": {
                  alignItems: "stretch",
                },
                "& .MuiDateRangeCalendar-root": {
                  alignSelf: "stretch",
                  flexGrow: 1,
                  height: "auto",
                },
                "& .MuiDateRangeCalendar-monthContainer": {
                  height: "100%",
                },
              },
            },
          }}
          calendars={2}
          {...props}
        />
      </DateDraftGateContext.Provider>
    </LocalizationProvider>
  );
}
export default DateRangeCalendarFilter;
